import { getExtensionValue } from "@medplum/core";
import { CodeableConcept, Coding, Encounter } from "@medplum/fhirtypes";

import { format, isValid, parseISO } from "date-fns";

import { Extensions } from "@/fhir/enums/extensions";
import { DEFAULT_SERVICE_DATE_TIMEZONE, isValidTimezone } from "@/shared/practice-timezone";
import { ServiceDateKey, toServiceDateKey } from "@/shared/procedure-code-effective-dates";
import {
  formatDateAtGivenPrecision,
  formatDateInTimezone,
  hasTimeComponent,
  US_DATE_FORMAT,
  US_DATE_TIME_WITH_ZONE_FORMAT,
} from "@/utils/datetime";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { toServiceDateTimezoneFormValue } from "./service-date-timezone-form";

/**
 * Reads the persisted service-date timezone from an encounter extension.
 *
 * @param encounter Encounter resource that may store a timezone extension.
 * @returns IANA timezone coding when present.
 */
export function getEncounterServiceDateTimezone(encounter?: Encounter): Coding | undefined {
  if (!encounter) return undefined;

  const timezoneExtension = getExtensionValue(encounter, Extensions.Timezone) as
    | CodeableConcept
    | undefined;

  return timezoneExtension?.coding?.[0];
}

/** Encounters already reported this session; the formatter runs on every render. */
const reportedInvalidTimezoneEncounterIds = new Set<string>();

/**
 * Reports, once per encounter, a stored timezone that could not be used. Only the encounter id is
 * sent, never the stored value or any service date.
 */
function reportInvalidEncounterTimezone(encounterId: string | undefined): void {
  const reportKey = encounterId ?? "";
  if (reportedInvalidTimezoneEncounterIds.has(reportKey)) return;
  reportedInvalidTimezoneEncounterIds.add(reportKey);
  captureExceptionWithoutPhi(
    "Encounter timezone is invalid; service date shown in the practice default timezone",
    undefined,
    { feature: "encounter-timezone", encounterId },
    "warning",
  );
}

/**
 * Formats one of an encounter's period instants in the encounter's stored timezone,
 * falling back to the practice default when no timezone extension is present.
 *
 * Callers establish that both the encounter and the instant exist, so neither is optional here.
 *
 * @param encounter Encounter resource carrying the optional timezone extension.
 * @param isoDateTime Instant to format, typically period.start or period.end.
 * @param formatStr date-fns format pattern; defaults to {@link US_DATE_FORMAT}.
 * @returns Formatted string in the encounter timezone, or empty string when the instant is invalid.
 */
function formatInEncounterTimezone(
  encounter: Encounter,
  isoDateTime: string,
  formatStr: string = US_DATE_FORMAT,
): string {
  // A date-only value is already the local service date; a timezone conversion could shift it.
  if (!hasTimeComponent(isoDateTime)) {
    const parsedDate = parseISO(isoDateTime);
    return isValid(parsedDate) ? format(parsedDate, formatStr) : "";
  }
  const storedCode = toServiceDateTimezoneFormValue(
    getEncounterServiceDateTimezone(encounter),
  ).code;
  // A malformed timezone extension would make formatting throw, so it falls back to the default,
  // and the bad data is reported so it can be fixed.
  if (!isValidTimezone(storedCode)) reportInvalidEncounterTimezone(encounter.id);
  const timezoneCode = isValidTimezone(storedCode)
    ? storedCode
    : DEFAULT_SERVICE_DATE_TIMEZONE.code;
  return formatDateInTimezone(isoDateTime, timezoneCode, formatStr);
}

/**
 * Formats the encounter service date (period.start) in the encounter's stored timezone.
 *
 * @param encounter Encounter resource with period.start and optional timezone extension.
 * @returns Date string in MM/dd/yyyy in the encounter timezone, or empty string when period.start is absent.
 */
export function formatEncounterServiceDate(encounter: Encounter | undefined): string {
  if (!encounter?.period?.start) return "";
  return formatInEncounterTimezone(encounter, encounter.period.start);
}

const FULL_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Returns the encounter service date (period.start) as `yyyy-MM-dd` in the encounter's stored
 * timezone, the same date {@link formatEncounterServiceDate} shows.
 *
 * @param encounter Encounter resource with period.start and optional timezone extension.
 * @returns The service date, or undefined when period.start is absent, invalid, or has only year
 *   or month precision, so no date rule applies
 */
export function getEncounterServiceDateKey(
  encounter: Encounter | undefined,
): ServiceDateKey | undefined {
  const start = encounter?.period?.start;
  if (!start || !FULL_DATE_PREFIX.test(start)) return undefined;
  return toServiceDateKey(formatInEncounterTimezone(encounter, start, "yyyy-MM-dd"));
}

/**
 * Formats the encounter service end date (period.end) in the encounter's stored timezone.
 *
 * @param encounter Encounter resource with period.end and optional timezone extension.
 * @returns Date string in MM/dd/yyyy in the encounter timezone, or empty string when period.end is absent.
 */
export function formatEncounterServiceEndDate(encounter: Encounter | undefined): string {
  if (!encounter?.period?.end) return "";
  return formatInEncounterTimezone(encounter, encounter.period.end);
}

/**
 * Formats the encounter service date for display alongside its time of day.
 *
 * Encounter headers show the service date in the client's timezone, so the time is meaningless
 * without the zone label next to it; both are rendered as one value. Encounters stored with
 * date-only precision have no time to show, so those render as the date alone.
 *
 * @param encounter Encounter resource with period.start and optional timezone extension.
 * @returns "MM/dd/yyyy HH:mm ZZZ" in the encounter timezone, the date at its own precision when
 *   period.start is date-only, or empty string when period.start is absent.
 */
export function formatEncounterServiceDateTime(encounter: Encounter | undefined): string {
  if (!encounter?.period?.start) return "";

  const periodStart = encounter.period.start;

  // A date-only value names a calendar date with no time of day and no offset, so it is already the
  // date to show; converting it through a timezone would shift it by a day.
  if (!hasTimeComponent(periodStart)) return formatDateAtGivenPrecision(periodStart);

  return formatInEncounterTimezone(encounter, periodStart, US_DATE_TIME_WITH_ZONE_FORMAT);
}
